"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, EyeOff, GraduationCap, Keyboard, X } from "lucide-react";
import { ModalOverlay } from "@/components/ui/atoms/modal-overlay";
import { VirtuosaChordKeyboard } from "@/components/ui/organisms/virtuosa-chord-keyboard";
import { buildLayout } from "@/lib/virtuosa-layout/layout";
import { placeChord } from "@/lib/virtuosa-layout/place-chord";
import {
  getValidScaleOctaves,
  placeScale,
} from "@/lib/virtuosa-layout/place-scale";
import type { ScaleQuality } from "@/lib/virtuosa-layout/place-scale";
import {
  AZERTY_LABELS,
  resolveLayoutLabels,
} from "@/lib/virtuosa-layout/labels";
import type {
  ChordQuality,
  LayoutCode,
  LayoutKey,
} from "@/lib/virtuosa-layout/types";
import type { LabelMode } from "@/components/ui/organisms/virtuosa-chord-keyboard";
import { useActivePianoNotes } from "@/hooks/use-active-piano-notes";
import { useCurrentHint } from "@/contexts/hint-context";
import { usePianoPressSequence } from "@/hooks/use-piano-press-sequence";
import { usePianoPressSet } from "@/hooks/use-piano-press-set";
import { useStudio } from "@/contexts/studio-context";
import { Note } from "@/lib/music/note";
import {
  useKeyboardEnabled,
  useMicActive,
  useMidiInputs,
} from "@/contexts/studio-context";
import { ChordPicker } from "./chord-picker";
import { HintFabPill } from "./hint-fab-pill";
import { ScalePicker } from "./scale-picker";
import { PressedPill } from "./pressed-pill";
import { TutorialPanel } from "./tutorial-panel";
import { TUTORIAL_STEPS } from "./tutorial-data";

type Mode = "tutorial" | "chord" | "scale";

const TUTORIAL_DONE_KEY = "virtuosa-layout.tutorial-completed";
const HINT_PILL_KEY = "virtuosa-layout.hint-pill-enabled";
const LABEL_MODE_KEY = "virtuosa-layout.label-mode";

// How fast the invariance demonstration walks the twelve keys. Slower under a
// reduced-motion preference rather than disabled: the movement *is* the
// content of that step, so removing it would remove the lesson.
const SWEEP_MS = 700;
const SWEEP_MS_REDUCED = 1800;

const LABEL_MODES: { id: LabelMode; label: string; title: string }[] = [
  {
    id: "key-and-note",
    label: "Touche + note",
    title: "Chaque case porte sa lettre et son nom de note",
  },
  {
    id: "key",
    label: "Touche",
    title: "Seulement la lettre : on se repère par les règles, plus par le nom",
  },
  {
    id: "none",
    label: "Rien",
    title: "Aucun texte : la forme seule",
  },
];

function readLabelMode(): LabelMode {
  if (typeof window === "undefined") {
    return "key-and-note";
  }

  try {
    const v = window.localStorage.getItem(LABEL_MODE_KEY);

    return v === "key" || v === "none" ? v : "key-and-note";
  } catch {
    return "key-and-note";
  }
}

function writeLabelMode(mode: LabelMode): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(LABEL_MODE_KEY, mode);
  } catch {
    // Ignore quota / privacy errors.
  }
}

// When a note has two cells, this is the one the helper points at: the one
// nearest the home row, where the hands already are. It is the same rule
// `pickStartingRoot` uses in place-scale.ts, and using the same one keeps the
// hint and the scale path from disagreeing about where a note "is".
function preferredCell(cells: readonly LayoutKey[]): LayoutKey {
  return cells.reduce(
    (best, k) => (Math.abs(k.row - 2) < Math.abs(best.row - 2) ? k : best),
    cells[0],
  );
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Stable identity: usePianoPressSequence resets its cursor whenever the
// expected array changes, so an inline [] would reset it on every render.
const NO_SEQUENCE: readonly number[] = [];

/** Softer than a played note: clicking a cell is a look, not a performance. */
const CLICK_VELOCITY = 0.45;

function readTutorialDone(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem(TUTORIAL_DONE_KEY) === "1";
  } catch {
    return false;
  }
}

function markTutorialDone(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(TUTORIAL_DONE_KEY, "1");
  } catch {
    // Ignore quota / privacy errors.
  }
}

function readHintPillEnabled(): boolean {
  if (typeof window === "undefined") {
    return true;
  }

  try {
    const v = window.localStorage.getItem(HINT_PILL_KEY);

    return v === null ? true : v === "1";
  } catch {
    return true;
  }
}

function writeHintPillEnabled(enabled: boolean): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(HINT_PILL_KEY, enabled ? "1" : "0");
  } catch {
    // Ignore quota / privacy errors.
  }
}

/**
 * Coque : ne lit que ce que la garde regarde.
 *
 * Le corps, lui, s'abonne au piano (notes actives, séquences, accords du
 * tutoriel) et se re-rendait à chaque touche enfoncée ou relâchée — pour
 * rendre `null` dès qu'un micro, un MIDI ou un clavier coupé le masquait.
 * Séparer la garde du corps démonte tout ça quand l'aide n'a rien à montrer.
 *
 * Rien du corps n'a besoin de survivre au masquage : ses préférences
 * (tutoriel fait, pastille, étiquettes) sont relues depuis `localStorage` au
 * remontage, et l'`Escape` n'a de sens que fenêtre ouverte, donc corps monté.
 */
export function VirtuosaLayoutHelper() {
  const micActive = useMicActive();
  const { devices: midiDevices } = useMidiInputs();
  const midiActive = midiDevices.some((d) => d.enabled && d.connected);
  const [keyboardEnabled] = useKeyboardEnabled();

  if (micActive || midiActive || !keyboardEnabled) {
    return null;
  }

  return <VirtuosaLayoutHelperBody />;
}

function VirtuosaLayoutHelperBody() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>(() =>
    readTutorialDone() ? "chord" : "tutorial",
  );
  const [tutorialStep, setTutorialStep] = useState(0);

  const [rootSemi, setRootSemi] = useState(0);
  const [quality, setQuality] = useState<ChordQuality>("maj");
  const [withBass, setWithBass] = useState(true);
  const [scaleRootSemi, setScaleRootSemi] = useState(0);
  const [scaleQuality, setScaleQuality] = useState<ScaleQuality>("major");
  const [scaleOctave, setScaleOctave] = useState(3);
  const [hintPillEnabled, setHintPillEnabled] = useState<boolean>(() =>
    readHintPillEnabled(),
  );
  const [labels, setLabels] = useState<Record<LayoutCode, string>>(
    () => AZERTY_LABELS,
  );
  const [labelMode, setLabelMode] = useState<LabelMode>(() => readLabelMode());
  const [sweepRootSemi, setSweepRootSemi] = useState(0);

  const { studio } = useStudio();

  const layout = useMemo(() => buildLayout(), []);
  const activeMidis = useActivePianoNotes();

  useEffect(() => {
    let cancelled = false;
    const codes = Array.from(layout.byCode.keys());

    resolveLayoutLabels(codes).then((resolved) => {
      if (!cancelled) {
        setLabels(resolved);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [layout]);

  const placement = useMemo(
    () => placeChord({ rootSemi, quality, withBass }, layout),
    [rootSemi, quality, withBass, layout],
  );

  const validScaleOctaves = useMemo(
    () => getValidScaleOctaves(scaleRootSemi, layout),
    [scaleRootSemi, layout],
  );

  const effectiveScaleOctave = validScaleOctaves.includes(scaleOctave)
    ? scaleOctave
    : (validScaleOctaves[0] ?? 3);

  const scalePlacement = useMemo(
    () =>
      placeScale(scaleRootSemi, scaleQuality, layout, effectiveScaleOctave),
    [scaleRootSemi, scaleQuality, layout, effectiveScaleOctave],
  );

  const scalePathOrder = useMemo<ReadonlyMap<LayoutCode, number> | undefined>(() => {
    if (mode !== "scale" || !scalePlacement) {
      return undefined;
    }

    const map = new Map<LayoutCode, number>();
    scalePlacement.keys.forEach((k, i) => {
      if (!map.has(k.code)) {
        map.set(k.code, i + 1);
      }
    });

    return map;
  }, [mode, scalePlacement]);

  const scaleHintCodes = useMemo<ReadonlySet<LayoutCode> | undefined>(() => {
    if (mode !== "scale" || !scalePlacement) {
      return undefined;
    }

    return new Set(scalePlacement.keys.map((k) => k.code));
  }, [mode, scalePlacement]);

  // Whatever the rest of the app is currently pointing at, mapped onto this
  // keyboard. The helper had its own two sources — its scale mode and its
  // tutorial — and consumed the shared `HintContext` not at all, so every
  // hint published by the learning journey lit the studio piano and left
  // this one dark.
  //
  // One midi can carry several codes: the lattice repeats note sets across
  // rows, and a player looking for a key wants all the places it is.
  const currentHint = useCurrentHint();

  const journeyHintCodes = useMemo<ReadonlySet<LayoutCode> | undefined>(() => {
    if (!currentHint || currentHint.midis.length === 0) {
      return undefined;
    }

    const set = new Set<LayoutCode>();

    for (const { midi } of currentHint.midis) {
      for (const k of layout.byMidi.get(midi) ?? []) {
        set.add(k.code);
      }
    }

    // A hint entirely outside the lattice — this keyboard covers F#2 to F#5 —
    // lights nothing, and saying so as `undefined` keeps the legend honest
    // rather than showing an "à jouer" key for keys that are not there.
    return set.size > 0 ? set : undefined;
  }, [currentHint, layout]);

  // Of the several cells that can carry a hinted note, one is shown as the
  // place to go and the rest as hollow echoes. Nine notes sit on two rows, so
  // without this a three-note hint can light six cells and the shape the
  // player is supposed to hold is not the shape on the screen.
  //
  // Only hints get echoes. A pressed note also lights every cell that carries
  // it, but there the helper genuinely does not know which key the fingers
  // came down on — the piano reports notes, not key codes — and inventing a
  // primary would light the wrong one half the time.
  const journeyEchoCodes = useMemo<ReadonlySet<LayoutCode> | undefined>(() => {
    if (!currentHint || currentHint.midis.length === 0) {
      return undefined;
    }

    const set = new Set<LayoutCode>();

    for (const { midi } of currentHint.midis) {
      const cells = layout.byMidi.get(midi) ?? [];

      if (cells.length < 2) {
        continue;
      }

      const primary = preferredCell(cells);

      for (const k of cells) {
        if (k.code !== primary.code) {
          set.add(k.code);
        }
      }
    }

    return set.size > 0 ? set : undefined;
  }, [currentHint, layout]);

  const pressedCodes = useMemo<ReadonlySet<LayoutCode>>(() => {
    const set = new Set<LayoutCode>();

    for (const midi of activeMidis) {
      const keys = layout.byMidi.get(midi);

      if (!keys) {
        continue;
      }

      for (const k of keys) {
        set.add(k.code);
      }
    }

    return set;
  }, [activeMidis, layout]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const currentTutorialStep = TUTORIAL_STEPS[tutorialStep] ?? null;

  const sequenceMidis = useMemo<readonly number[]>(() => {
    if (mode !== "tutorial" || currentTutorialStep?.kind !== "play-sequence") {
      return NO_SEQUENCE;
    }

    return currentTutorialStep.midis;
  }, [mode, currentTutorialStep]);

  const sequence = usePianoPressSequence(sequenceMidis);

  // The chord the current step asks for, and the notes it is made of. Both are
  // memoised on the step so the press-set hook keeps its progress instead of
  // resetting on every render.
  const tutorialChordPlacement = useMemo(() => {
    if (mode !== "tutorial" || currentTutorialStep?.kind !== "press-chord") {
      return null;
    }

    return placeChord(
      {
        rootSemi: currentTutorialStep.rootSemi,
        quality: currentTutorialStep.quality,
        withBass: currentTutorialStep.withBass,
      },
      layout,
    );
  }, [mode, currentTutorialStep, layout]);

  const tutorialChordMidis = useMemo<readonly number[]>(() => {
    if (!tutorialChordPlacement) {
      return NO_SEQUENCE;
    }

    return [...tutorialChordPlacement.left, ...tutorialChordPlacement.right]
      .map((k) => k.midi)
      .sort((a, b) => a - b);
  }, [tutorialChordPlacement]);

  const tutorialChordCodes = useMemo<ReadonlySet<LayoutCode> | undefined>(() => {
    if (!tutorialChordPlacement) {
      return undefined;
    }

    return new Set(
      [...tutorialChordPlacement.left, ...tutorialChordPlacement.right].map(
        (k) => k.code,
      ),
    );
  }, [tutorialChordPlacement]);

  const chordSet = usePianoPressSet(tutorialChordMidis);

  // The invariance demonstration: the same shape walked across the twelve
  // roots. It is the only place the tutorial shows rather than asserts the
  // property the whole lattice was chosen for.
  const isSweeping =
    mode === "tutorial" && currentTutorialStep?.kind === "chord-sweep";

  // Restart the walk at Do whenever the sweep step is (re)entered. Done during
  // render rather than in the effect below: the effect owns the interval, and
  // seeding state from it would show one frame of the previous run's position.
  const [sweptStepId, setSweptStepId] = useState<string | null>(null);
  const activeSweepId = isSweeping ? (currentTutorialStep?.id ?? null) : null;

  if (sweptStepId !== activeSweepId) {
    setSweptStepId(activeSweepId);
    setSweepRootSemi(0);
  }

  useEffect(() => {
    if (!isSweeping) {
      return;
    }

    const period = prefersReducedMotion() ? SWEEP_MS_REDUCED : SWEEP_MS;
    const id = window.setInterval(
      () => setSweepRootSemi((r) => (r + 1) % 12),
      period,
    );

    return () => window.clearInterval(id);
  }, [isSweeping]);

  const sweepPlacement = useMemo(() => {
    if (!isSweeping || currentTutorialStep?.kind !== "chord-sweep") {
      return null;
    }

    return placeChord(
      {
        rootSemi: sweepRootSemi,
        quality: currentTutorialStep.quality,
        withBass: currentTutorialStep.withBass,
      },
      layout,
    );
  }, [isSweeping, currentTutorialStep, sweepRootSemi, layout]);

  const tutorialHintCodes = useMemo<ReadonlySet<LayoutCode> | undefined>(() => {
    if (mode !== "tutorial" || !currentTutorialStep) {
      return undefined;
    }

    if (currentTutorialStep.kind === "info") {
      return undefined;
    }

    if (currentTutorialStep.kind === "press-code") {
      return new Set(currentTutorialStep.hintCodes);
    }

    if (currentTutorialStep.kind === "press-chord") {
      return tutorialChordCodes;
    }

    if (currentTutorialStep.kind === "chord-sweep") {
      // The swept chord is carried by `highlight`, which already colours the
      // hands; adding a hint ring on top would only hide that.
      return undefined;
    }

    // A whole scale lit up at once is unreadable: only point at the next note.
    // hintCodes is parallel to midis / noteNames.
    const next = currentTutorialStep.hintCodes[sequence.progress];

    return new Set(next ? [next] : []);
  }, [mode, currentTutorialStep, sequence.progress, tutorialChordCodes]);

  const tutorialAnchorCodes = useMemo<ReadonlySet<LayoutCode> | undefined>(() => {
    if (mode !== "tutorial" || !currentTutorialStep) {
      return undefined;
    }

    if (currentTutorialStep.kind === "press-code") {
      return currentTutorialStep.fromCodes
        ? new Set(currentTutorialStep.fromCodes)
        : undefined;
    }

    if (currentTutorialStep.kind === "press-chord") {
      return currentTutorialStep.movedCodes
        ? new Set(currentTutorialStep.movedCodes)
        : undefined;
    }

    return undefined;
  }, [mode, currentTutorialStep]);

  const advanceTutorial = useCallback((): void => {
    setTutorialStep((s) => {
      const next = s + 1;
      if (next >= TUTORIAL_STEPS.length) {
        markTutorialDone();
        setMode("chord");

        return s;
      }

      return next;
    });
  }, []);

  const skipTutorial = useCallback((): void => {
    markTutorialDone();
    setMode("chord");
  }, []);

  const startTutorial = useCallback((): void => {
    setTutorialStep(0);
    setMode("tutorial");
  }, []);

  // The 46 cells were <button>s with a pointer cursor and a hover state whose
  // handlers were declared and never passed: a clickable-looking control that
  // did nothing, forty-six times. Sounding the note is also the shortest path
  // there is to tying a position to a pitch.
  //
  // Pressed as the application, not as the player: the learning journey's
  // watcher ignores "app" presses, so exploring the reference keyboard cannot
  // be mistaken for answering the exercise underneath it. The tutorial's own
  // progress hooks do not filter, so clicking still walks a step forward.
  const clickHeld = useRef<Set<number>>(new Set());

  const playKey = useCallback(
    (key: LayoutKey): void => {
      const piano = studio?.getPiano();

      if (!piano) {
        return;
      }

      const note = Note.fromMidi(key.midi);

      if (!piano.contains(note)) {
        return;
      }

      piano.press(note, CLICK_VELOCITY, "app");
      clickHeld.current.add(key.midi);
    },
    [studio],
  );

  const releaseKey = useCallback(
    (key: LayoutKey): void => {
      studio?.getPiano()?.release(Note.fromMidi(key.midi), "app");
      clickHeld.current.delete(key.midi);
    },
    [studio],
  );

  // Closing the modal mid-click would otherwise leave the note sounding: the
  // pointer-up that would have lifted it lands on a element that no longer
  // exists. Only notes this component pressed are lifted — `Piano.release` is
  // origin-guarded, so a key the player is physically holding is untouched.
  useEffect(() => {
    if (open) {
      return;
    }

    const piano = studio?.getPiano();

    for (const midi of clickHeld.current) {
      piano?.release(Note.fromMidi(midi), "app");
    }

    clickHeld.current.clear();
  }, [open, studio]);

  const cycleLabelMode = useCallback((next: LabelMode): void => {
    setLabelMode(next);
    writeLabelMode(next);
  }, []);

  const toggleHintPill = useCallback((): void => {
    setHintPillEnabled((prev) => {
      const next = !prev;
      writeHintPillEnabled(next);

      return next;
    });
  }, []);

  return (
    <>
      {/*
        Masqué sur téléphone : c'est une aide au mapping du clavier
        d'ordinateur, sans objet là où il n'y en a pas, et c'est exactement la
        fenêtre où `HandheldNotice` occupe la vue — le pavé y flottait
        par-dessus, à z-index égal mais rendu plus tard.

        Les deux conditions sont celles du calque, et doivent le rester : le
        portrait étroit pour le téléphone debout, la hauteur sous 480 px pour le
        téléphone couché, que la largeur ne sait pas distinguer d'une tablette.

        `pointer-coarse:` fait partie de chacune, et pas par symétrie
        décorative : `orientation` et `max-height` décrivent le viewport, donc
        une fenêtre desktop étroite ou simplement basse y répond « oui ». Sans
        ce segment, l'utilisateur souris-clavier — le seul pour qui cette aide
        existe — la voyait disparaître alors que le studio restait parfaitement
        utilisable, et rien ne l'expliquait.
      */}
      <div className="portrait:max-md:pointer-coarse:hidden [@media(max-height:480px)]:pointer-coarse:hidden fixed bottom-4 right-4 z-40 flex items-center gap-2">
        {!open && hintPillEnabled ? <HintFabPill /> : null}
        {!open ? <PressedPill midis={activeMidis} /> : null}

        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Afficher le Virtuosa Layout"
          title="Virtuosa Layout"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-bg-elevated text-text-secondary border border-border-subtle shadow-lg transition-all duration-150 hover:bg-bg-hover hover:text-text-primary hover:scale-105 cursor-pointer"
        >
          <Keyboard size={18} />
        </button>
      </div>

      {open ? (
        <ModalOverlay onClick={() => setOpen(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-[min(95vw,900px)] rounded-2xl border p-6 shadow-2xl"
            style={{
              backgroundColor: "var(--color-glass-surface)",
              borderColor: "var(--color-glass-border)",
            }}
          >
            <div className="mb-4 flex items-start justify-between gap-6">
              <div>
                <h2 className="text-base font-semibold text-text-primary">
                  Virtuosa Layout
                </h2>
                <p className="mt-1 text-xs text-text-secondary">
                  Une colonne à droite <span className="font-mono">+3</span> ·
                  une rangée au-dessus <span className="font-mono">+1</span> ·
                  l&apos;octave <span className="font-mono">4 colonnes</span> ·
                  espace = sustain
                </p>
              </div>

              <div className="flex items-center gap-1">
                {mode !== "tutorial" ? (
                  <div className="flex items-center rounded-lg border border-border-subtle p-0.5">
                    <ModeTab
                      active={mode === "chord"}
                      onClick={() => setMode("chord")}
                      label="Accord"
                    />
                    <ModeTab
                      active={mode === "scale"}
                      onClick={() => setMode("scale")}
                      label="Gamme"
                    />
                  </div>
                ) : null}

                {mode !== "tutorial" ? (
                  <button
                    type="button"
                    onClick={startTutorial}
                    title="Relancer le tutoriel"
                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] text-text-secondary border border-border-subtle hover:bg-bg-hover hover:text-text-primary cursor-pointer"
                  >
                    <GraduationCap size={13} />
                    Tutoriel
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={toggleHintPill}
                  title={
                    hintPillEnabled
                      ? "Masquer l'indication clavier près du bouton"
                      : "Afficher l'indication clavier près du bouton"
                  }
                  aria-pressed={hintPillEnabled}
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border cursor-pointer ${
                    hintPillEnabled
                      ? "border-amber-300/60 bg-amber-300/15 text-amber-200 hover:bg-amber-300/25"
                      : "border-border-subtle text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                  }`}
                >
                  {hintPillEnabled ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>

                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Fermer"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-hover hover:text-text-primary cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="mb-4">
              {mode === "tutorial" && currentTutorialStep ? (
                <TutorialPanel
                  step={currentTutorialStep}
                  stepIndex={tutorialStep}
                  totalSteps={TUTORIAL_STEPS.length}
                  pressedCodes={pressedCodes}
                  sequence={sequence}
                  chordSet={chordSet}
                  chordMidis={tutorialChordMidis}
                  sweepRootSemi={sweepRootSemi}
                  onAdvance={advanceTutorial}
                  onSkip={skipTutorial}
                />
              ) : mode === "scale" ? (
                <ScalePicker
                  rootSemi={scaleRootSemi}
                  quality={scaleQuality}
                  octave={effectiveScaleOctave}
                  validOctaves={validScaleOctaves}
                  placement={scalePlacement}
                  onRootChange={setScaleRootSemi}
                  onQualityChange={setScaleQuality}
                  onOctaveChange={setScaleOctave}
                />
              ) : (
                <ChordPicker
                  rootSemi={rootSemi}
                  quality={quality}
                  withBass={withBass}
                  placement={placement}
                  onRootChange={setRootSemi}
                  onQualityChange={setQuality}
                  onWithBassChange={setWithBass}
                />
              )}
            </div>

            <VirtuosaChordKeyboard
              layout={layout}
              highlight={
                mode === "chord"
                  ? placement
                  : (sweepPlacement ?? tutorialChordPlacement)
              }
              pressed={pressedCodes}
              hintCodes={
                mode === "scale"
                  ? scaleHintCodes
                  : mode === "tutorial"
                    ? tutorialHintCodes
                    : journeyHintCodes
              }
              // Only the journey's hints need disambiguating: they arrive as
              // bare MIDIs and expand to every cell that carries them. The
              // scale path and the tutorial already name the cell they mean.
              echoCodes={mode === "chord" ? journeyEchoCodes : undefined}
              anchorCodes={tutorialAnchorCodes}
              pathOrder={scalePathOrder}
              labels={labels}
              labelMode={labelMode}
              showFamilies={
                mode === "tutorial" &&
                currentTutorialStep?.kind === "info" &&
                currentTutorialStep.showFamilies === true
              }
              showOctaveAnchors={labelMode !== "key-and-note"}
              onKeyDown={playKey}
              onKeyUp={releaseKey}
              rootPitchClass={
                mode === "chord"
                  ? rootSemi
                  : mode === "scale"
                    ? scaleRootSemi
                    : isSweeping
                      ? sweepRootSemi
                      : currentTutorialStep?.kind === "press-chord"
                        ? currentTutorialStep.rootSemi
                        : undefined
              }
            />

            <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] text-text-muted">
              <LegendDot color="var(--color-hand-left)" label="Main gauche" />
              <LegendDot color="var(--color-hand-right)" label="Main droite" />
              <LegendDot color="var(--color-accent)" label="Pressé" />
              {mode === "tutorial" ||
              mode === "scale" ||
              journeyHintCodes !== undefined ? (
                <LegendDot color="rgb(252,211,77)" label="À jouer" />
              ) : null}
              {journeyEchoCodes !== undefined && mode === "chord" ? (
                <span className="italic">
                  contour pointillé = la même note ailleurs
                </span>
              ) : null}

              <div className="ml-auto flex items-center gap-1">
                <span className="text-[10px] uppercase tracking-wider">
                  Étiquettes
                </span>
                <div className="flex items-center rounded-lg border border-border-subtle p-0.5">
                  {LABEL_MODES.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => cycleLabelMode(m.id)}
                      title={m.title}
                      aria-pressed={labelMode === m.id}
                      className={`rounded-md px-2 py-0.5 text-[10px] transition-all duration-100 cursor-pointer ${
                        labelMode === m.id
                          ? "bg-accent/20 text-accent-bright"
                          : "text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </ModalOverlay>
      ) : null}
    </>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span>{label}</span>
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-2.5 py-1 text-[11px] transition-all duration-100 cursor-pointer ${
        active
          ? "bg-accent/20 text-accent-bright"
          : "text-text-secondary hover:bg-bg-hover hover:text-text-primary"
      }`}
    >
      {label}
    </button>
  );
}
